import { IBandUserSelectOption } from "Bands/containers/PeoplePicker/useLogic";
import { useBandContext } from "Bands/contexts";
import { IBand, IBandUser } from "Bands/types";
import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { User } from "shared/assets/icons";
import { FormBuilder, Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IJSONAPIErrorCodes, IStatus } from "shared/types";
import { dataMetaSelector, dispatchGetVenueAction, inviteEditors } from "Venues/state";
import { IVenue, VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

import { getFields as getFormFields } from "./formData";

interface InviteEditorsModalProps {
  title: string;
  venue: IVenue;
  handleClose: () => void;
}

export const InviteEditorsModal: FC<InviteEditorsModalProps> = ({ title, venue, handleClose }) => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(dataMetaSelector(VENUE_ACTIONS.inviteEditors));
  const { userBands } = useBandContext();

  useClearActionMetaOnUnMount(VENUE_ACTIONS.inviteEditors);

  const onSubmit = ({ bandUsers }: { bandUsers: IBandUserSelectOption[] }) => {
    dispatch(
      inviteEditors({
        venueId: venue.id,
        bandUsers: bandUsers.map((option: IBandUserSelectOption) =>
          API_ENDPOINTS.BAND_USER(option.value)
        ),
      })
    );
  };

  const buttons = useMemo(
    () => ({
      primary: {
        children: copy.venueEditors.save,
        "data-testid": "invite_editors_submit",
      },
      secondary: {
        onClick: handleClose,
      },
    }),
    [handleClose]
  );

  // on success show success message and close modal
  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          message: copy.venueEditors.invite.success,
          status: IStatus.success,
        })
      );
      // refetch venue to update invites and users
      dispatchGetVenueAction(dispatch, venue.id);

      handleClose();
    }
  }, [apiMeta, venue.id, dispatch, handleClose]);

  const errorMessage = useMemo(() => {
    if (apiMeta?.error) {
      const { errorCodes } = apiMeta.error;

      if (errorCodes.includes(IJSONAPIErrorCodes.NotUniqueError)) {
        return copy.venueEditors.invite.errors.alreadyInvite;
      }
      if (errorCodes.includes(IJSONAPIErrorCodes.IsEditor)) {
        return copy.venueEditors.invite.errors.alreadyEditor;
      }
      return copy.errors.general;
    }
    return false;
  }, [apiMeta?.error]);

  const allBandsUsers = useMemo(
    () => userBands.map(({ users }: IBand) => users).flat() as IBandUser[],
    [userBands]
  );

  const excludeOptions = useMemo(
    () =>
      venue.users &&
      (venue.users.map(user => {
        const found = allBandsUsers.find(
          bandUser => bandUser?.user && bandUser?.user.id === user.id
        );

        return found?.id;
      }) as string[]),
    [venue.users, allBandsUsers]
  );

  return (
    <Modal
      handleClose={handleClose}
      title={`${copy.venueEditors.invite.title} ${title}`}
      icon={User}
    >
      <FormBuilder
        onSubmit={onSubmit}
        fields={getFormFields({ excludeOptions })}
        buttons={buttons}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        apiErrorMessage={errorMessage}
      />
    </Modal>
  );
};
